import { KeyRound, Pencil } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { Platform } from "../domain/types";
import { PlatformBadge } from "../components/PlatformBadge";
import { Button, Field, Modal, PageHeader, StatusChip } from "../components/UI";
import { useWeSell } from "../state/WeSellProvider";

function codeHelp(platform: Platform): string {
  if (platform.slug === "amazon")
    return "É o Partner Tag que aparece no parâmetro tag dos links Amazon.";
  if (platform.slug === "temu")
    return "Copie o código ou ID exibido na central Temu Affiliate. O T001 continua sendo o código curto público do item.";
  return "Copie o Affiliate ID da central SHEIN. Os links devem ser gerados dentro da área oficial de afiliados.";
}

export function Platforms() {
  const { data, saving, editPlatform } = useWeSell();
  const [selected, setSelected] = useState<Platform | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [affiliateUrl, setAffiliateUrl] = useState("");
  const [notes, setNotes] = useState("");
  if (!data) return null;

  const open = (platform: Platform) => {
    setSelected(platform);
    setReferralCode(platform.referralCode);
    setAffiliateUrl(platform.affiliateUrl);
    setNotes(platform.notes);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    await editPlatform(selected.id, { referralCode, affiliateUrl, notes });
    setSelected(null);
  };

  return (
    <>
      <PageHeader
        eyebrow="Configuração"
        title="Plataformas e códigos"
        description="Cadastre uma vez os identificadores oficiais usados para validar cada link de afiliada."
      />
      <aside className="info-banner">
        <KeyRound size={20} />
        <div>
          <strong>Dois códigos com funções diferentes</strong>
          <p>
            O código oficial identifica você para a loja e garante a comissão. O
            código curto do WeSell, como T001, identifica o item para quem está
            vendo seu vídeo.
          </p>
        </div>
      </aside>
      <div className="connection-grid">
        {data.platforms.map((platform) => (
          <article className="connection-card panel" key={platform.id}>
            <header>
              <PlatformBadge platform={platform} />
              <StatusChip
                status={platform.referralCode ? "ok" : "warning"}
                label={
                  platform.referralCode ? "Código configurado" : "Falta código"
                }
              />
            </header>
            <h2>{platform.referralCode || "Código ainda não cadastrado"}</h2>
            <p>{codeHelp(platform)}</p>
            <dl>
              <div>
                <dt>Base afiliada</dt>
                <dd>{platform.affiliateUrl || "Não cadastrada"}</dd>
              </div>
              <div>
                <dt>Automação</dt>
                <dd>
                  {platform.connectionMode === "official_api"
                    ? "API oficial"
                    : "Relatório e conector local"}
                </dd>
              </div>
            </dl>
            <Button onClick={() => open(platform)}>
              <Pencil size={17} /> Editar código
            </Button>
          </article>
        ))}
      </div>
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={
          selected ? `Configurar ${selected.name}` : "Configurar plataforma"
        }
        description={selected ? codeHelp(selected) : ""}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSelected(null)}>
              Cancelar
            </Button>
            <Button form="platform-form" type="submit" loading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        <form id="platform-form" className="form-grid" onSubmit={submit}>
          <Field label="Código oficial / Affiliate ID">
            <input
              value={referralCode}
              onChange={(event) => setReferralCode(event.target.value.trim())}
              required
            />
          </Field>
          <Field
            label="URL da central ou página afiliada"
            helper="Opcional. Ajuda você a voltar ao painel certo rapidamente."
          >
            <input
              type="url"
              value={affiliateUrl}
              onChange={(event) => setAffiliateUrl(event.target.value)}
            />
          </Field>
          <Field label="Notas">
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>
        </form>
      </Modal>
    </>
  );
}
